import { useState, createContext } from "react";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import {
  getCategoriesAdminService,
  createCategoryAdminService,
  deleteCategoryAdminService,
  editCategoryAdminService,
  changeCategoryActiveAdminService,
} from "../../../services/adminServices";

export const categoryAdminContext = createContext({
  categories: [],
  setCategories: () => {},
  subCategories: [],
  setSubCategories: () => {},
  category: {},
  setCategory: () => {},
  id: "",
  setId: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  isActive: "",
  setIsActive: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetCategories: () => {},
  handleCreateCategory: () => {},
  handleEditCategory: () => {},
  handleDeleteCategory: () => {},
  handleChangeCategoryActive: () => {},
});

const CategoryAdminContext = ({ children }) => {
  const dispatch = useDispatch();

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [category, setCategory] = useState({});
  const [id, setId] = useState("");
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [isActive, setIsActive] = useState("");
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setId(val._id);
      setValue("avatar", val.avatar);
      setValue("title", val.title);
      setValue("description", val.description);
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setId("");
  };

  // Get Categories
  const handleGetCategories = async (event, item) => {
    event?.preventDefault();

    const cat = item ?? category;

    const isSub = !!cat?._id;

    const objData = {
      sort,
      filter,
      ...(isActive && { isActive }),
      ...(start && { start }),
      ...(end && { end }),
      ...(isSub && { categoryId: cat?._id }),
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCategoriesAdminService(objData);
      if (status === 200) {
        if (isEmpty(cat)) setCategories(data.result?.categories);
        else {
          setSubCategories(data.result?.categories);
          setCategory(cat);
        }
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Category
  const handleCreateCategory = async (userData) => {
    const objData = {
      categoryId: category?._id,
      ...userData,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createCategoryAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCategories();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Category
  const handleEditCategory = async (userData) => {
    const objData = {
      id,
      ...userData,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editCategoryAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCategories();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Category
  const handleDeleteCategory = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteCategoryAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCategories();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Change Category Active
  const handleChangeCategoryActive = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeCategoryActiveAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCategories();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <categoryAdminContext.Provider
      value={{
        categories,
        setCategories,
        subCategories,
        setSubCategories,
        category,
        setCategory,
        id,
        setId,
        filter,
        setFilter,
        sort,
        setSort,
        start,
        setStart,
        end,
        setEnd,
        isActive,
        setIsActive,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetCategories,
        handleCreateCategory,
        handleEditCategory,
        handleDeleteCategory,
        handleChangeCategoryActive,
      }}
    >
      {children}
    </categoryAdminContext.Provider>
  );
};

export default CategoryAdminContext;
